import {
  Button,
  ChevronAndTitle,
  WarrantyInfo,
} from '@rabbit/elements/shared-components';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import ROUTE_NAME from '../../../utils/url-constants';
import placeholder from '../../../assets/images/no_products_yet.svg';
import { DTVendable } from '@rabbit/data/types';
import { getWarrantyInfo } from '@rabbit/olive/utils/helpers';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { AppContext } from '@rabbit/app-context';
import { subscribeDocument } from '@rabbit/utils/api';
import { BL_Warranty } from '@rabbit/bizproc/core';

export interface VendableInfoContainerProps {
  docid: string;
  onChangeStep?: (step_in: number, data?: any) => void;
  holdingId?: string | null;
}

export interface ItemProps {
  id?: string;
  title: string;
  description?: string;
  included?: string;
}

export interface InfoInterface {
  title: string;
  type: string;
  items?: ItemProps[];
  children?: any;
}

export function VendableInfoContainer({
  docid,
  onChangeStep,
}: VendableInfoContainerProps) {
  const { config } = useContext(AppContext);
  const [showWarrantyDetails, setShowWarrantyDetails] = useState(false);
  const [product, setProduct] = useState<DTVendable | undefined>(undefined);
  const [warrantyInfo, setWarrantyInfo] = useState<InfoInterface[]>([]);
  const [warrantyTerms, setWarrantyTerms] = useState<InfoInterface[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tenantInfo } = useContext(AppContext);
  const searchParams = new URLSearchParams(window.location.search);
  const params = useParams();
  const productPlaceHolder =
    t('CFG_COBRAND_LANDING_IMAGE_PRODUCT_PLACEHOLDER', { defaultValue: '' }) ||
    placeholder;

  useEffect(() => {
    const unSub = subscribeDocument<DTVendable>(
      `vendable/${localStorage.vendableId ?? docid}`,
      (data) => {
        setProduct(data.data());
        //localStorage.removeItem('vendableId');
      }
    );

    return () => {
      unSub();
    };
  }, [docid]);

  useEffect(() => {
    if (window) window.localStorage.removeItem('LRP_VendableId');
    const getWarranty = async () => {
      // Get warranty info and terms from the tenant base
      const warranty = await getWarrantyInfo(t('tenantLink'));
      // If warranty terms and info are from firestore, get them from the firestore
      if (config.WARRANTIES.WARRANTY_TC_FROM_FIRESTORE) {
        const warrantyTermsAndInfo = await BL_Warranty.getWarrantyTermsAndInfo(
          product?.defaultTemplate ?? ''
        );
        if (warrantyTermsAndInfo) {
          setWarrantyInfo(warrantyTermsAndInfo.info as InfoInterface[]);
          setWarrantyTerms(warrantyTermsAndInfo.terms as InfoInterface[]);
        } else {
          // If warranty terms and info are not from firestore, then use the tenant base
          setWarrantyInfo(warranty.warranty_info);
          setWarrantyTerms(warranty.warranty_terms);
        }
      } else {
        // Warranty terms and info according to the current tenant
        if (warranty) {
          setWarrantyInfo(warranty.warranty_info);
          setWarrantyTerms(warranty.warranty_terms);
        }
      }
    };
    void getWarranty();
  }, [t('tenantLink'), product?.defaultTemplate]);

  return (
    <div className="mt-6 px-4 sm:px-6 md:px-8">
      {!onChangeStep && (
        <ChevronAndTitle
          title={
            searchParams.get('holdingId')
              ? t('Complete registration')
              : t('Product information')
          }
          onClick={() => navigate(-1)}
        />
      )}
      <div
        className={`mt-8 grid grid-cols-1 gap-8 sm:mt-12 md:mt-16 md:grid-cols-2 md:gap-10 lg:gap-12`}
      >
        <div
          className={`h-fit min-h-[680px] w-full rounded-lg bg-white md:max-h-[680px] ${
            onChangeStep
              ? 'max-w-full'
              : 'transition-shadow duration-300 md:shadow-lg md:shadow-gray-200/50'
          } `}
        >
          <div className="flex flex-col">
            <div className="flex flex-col items-center justify-center p-6 sm:p-8">
              <div className="flex w-full flex-col">
                <div className="mb-6 flex justify-center">
                  <img
                    src={tenantInfo?.logo}
                    alt={tenantInfo?.name}
                    className="max-h-[48px] object-contain sm:max-h-[64px]"
                  />
                </div>

                <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg bg-gray-50 p-4 sm:aspect-auto sm:h-[300px] md:h-[320px] lg:h-[360px]">
                  <img
                    src={product?.img?.[0] ?? productPlaceHolder}
                    className="max-h-full max-w-full object-contain transition-transform duration-300 hover:scale-105"
                    alt={product?.title || 'Product image'}
                  />
                </div>
              </div>
              <div className="mt-4 flex w-full flex-col items-center sm:mt-4">
                <h2 className="font-nunito sm:text-md mb-0 max-w-md text-center text-xl font-bold capitalize text-gray-700 sm:mb-0">
                  {product?.title ?? ''}
                </h2>
                {config.VENDABLES.SKU_SEARCH && (
                  <h2 className="font-nunito mb-3 max-w-md text-center text-sm capitalize text-gray-700 sm:mb-3 sm:text-sm">
                    {product?.mpn ?? ''}
                  </h2>
                )}
                <div className="flex w-full flex-col sm:w-auto">
                  <Button
                    className="text-md sm:text-md w-full px-6 py-3 font-medium sm:w-auto sm:px-8"
                    children={
                      <>
                        {params.holdingId
                          ? t('View product')
                          : searchParams.get('holdingId')
                          ? t('Complete registration')
                          : !onChangeStep
                          ? t(`Register product`)
                          : t('Initiate claim')}
                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                      </>
                    }
                    kind={'primary'}
                    onClick={() => {
                      if (onChangeStep) {
                        window.localStorage.setItem(
                          'NLIC_VendableId',
                          product?.docid ?? ''
                        );
                        window.localStorage.setItem(
                          'NLIC_VendableName',
                          product?.title ?? ''
                        );
                        onChangeStep(4);
                      } else {
                        searchParams.get('holdingId')
                          ? navigate(
                              ROUTE_NAME.PRODUCTS_PREVIEW +
                                '/' +
                                product?.docid +
                                '/register?holdingId=' +
                                searchParams.get('holdingId')
                            )
                          : params.holdingId
                          ? navigate(
                              ROUTE_NAME.PRODUCTS +
                                '/' +
                                params.holdingId +
                                '/details'
                            )
                          : navigate(
                              ROUTE_NAME.PRODUCTS_PREVIEW +
                                '/' +
                                product?.docid +
                                '/register'
                            );
                      }
                    }}
                  />
                  {!onChangeStep && (
                    <div className="mt-4 flex w-full md:hidden">
                      <Button
                        className="w-full"
                        children={'Warranty information'}
                        kind={'outline'}
                        onClick={() =>
                          setShowWarrantyDetails(!showWarrantyDetails)
                        }
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          setShowWarrantyDetails(!showWarrantyDetails);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {!onChangeStep && (
          <div
            className={classNames(
              `${showWarrantyDetails ? 'block' : 'hidden'} rounded-lg transition-all duration-300 md:block md:max-h-[680px] md:bg-white md:shadow-lg md:shadow-gray-200/50`,
              {
                [`mt-6 bg-white shadow-lg shadow-gray-200/50`]:
                  showWarrantyDetails,
              }
            )}
          >
            <div className="flex h-full flex-col overflow-hidden">
              <div className="flex-none">
                <div className="text-center">
                  <div className="font-caudex flex h-[70px] items-center justify-center border-b border-gray-200 text-xl font-bold md:text-2xl">
                    {warrantyInfo?.length > 0 && (
                      <>
                        {tenantInfo?.name === 'NuCover'
                          ? 'Care Plan Summary'
                          : 'Warranty information'}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-grow overflow-y-auto">
                <div className="flex flex-col space-y-4 py-6 px-4 sm:px-6 md:px-8">
                  {warrantyInfo &&
                    warrantyInfo.map((data: InfoInterface, index: number) => {
                      return <WarrantyInfo key={index} data={data} />;
                    })}
                  {warrantyTerms &&
                    warrantyTerms.map((data: InfoInterface, index: number) => {
                      return <WarrantyInfo key={index} data={data} />;
                    })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default VendableInfoContainer;
